import React, { useState } from 'react';
import {
  ArrowRight,
  Clock,
  Calendar,
  Users2,
  Wallet,
  CheckCircle2,
  Palette,
  Music,
  Puzzle,
  Smile,
  BookOpen,
  Calculator,
  Heart,
  Leaf as LeafIcon,
  FlaskConical,
  Globe,
  Users,
  Dumbbell,
  School,
  Sparkles,
} from 'lucide-react';
import { programs } from '../../mock/data';
import { Cloud, Star, Bird, Flower } from './Decor';

const iconMap = {
  Palette, Music, Puzzle, Smile, BookOpen, Calculator,
  Heart, Leaf: LeafIcon, FlaskConical, Globe, Users, Dumbbell,
};

const colorMap = {
  peach: {
    bg: '#ffd7bf',
    soft: '#fff2e2',
    accent: '#ee7a3d',
    dark: '#b04a1e',
    ring: 'rgba(238,122,61,0.25)',
  },
  cream: {
    bg: '#ffe9a8',
    soft: '#fff6d9',
    accent: '#e2a12f',
    dark: '#8f6410',
    ring: 'rgba(226,161,47,0.25)',
  },
  sky: {
    bg: '#bde3ee',
    soft: '#e4f4f9',
    accent: '#3aa9b7',
    dark: '#1e6f7c',
    ring: 'rgba(58,169,183,0.25)',
  },
};

const Programs = () => {
  const [activeId, setActiveId] = useState(programs[0].id);
  const active = programs.find((p) => p.id === activeId);
  const c = colorMap[active.color];

  return (
    <section id="program" className="relative overflow-hidden pt-24 pb-32 bg-sky-soft">
      {/* Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <Cloud size={240} className="absolute top-10 left-[-30px] animate-cloud" />
        <Cloud size={200} className="absolute top-40 right-[-20px] animate-cloud-rev" />
        <Star size={30} className="absolute top-16 right-[20%] animate-twinkle" />
        <Star size={24} className="absolute top-24 left-[18%] animate-twinkle" color="#ffb27a" />
        <div className="absolute bottom-16 left-[4%] animate-bird"><Bird size={70} flip /></div>
        <Flower size={64} className="absolute bottom-24 right-[5%] animate-float" color="#f8c1c1" />
      </div>

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center">
          <div className="font-hand text-[15px] tracking-[0.35em] text-[#c74a2a] mb-2">PROGRAM KAMI</div>
          <h2 className="font-brush text-[46px] md:text-[64px] leading-[1] text-[#2b8b98]">
            Belajar Sesuai Usia
          </h2>
          <p className="mt-4 max-w-[680px] mx-auto font-hand text-[18px] text-[#4a3b2a]/85">
            Tiga jenjang program yang dirancang khusus, agar setiap anak belajar sesuai
            tahap perkembangannya dengan cara yang paling ceria.
          </p>
        </div>

        {/* Overview cards */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {programs.map((p, i) => {
            const cc = colorMap[p.color];
            const isActive = p.id === activeId;
            return (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                className={`group relative text-left rounded-[32px] p-8 pt-14 bg-white border-2 transition-all duration-500 ${
                  isActive
                    ? 'border-transparent -translate-y-3 shadow-[0_14px_0_rgba(120,180,200,0.35)]'
                    : 'border-white shadow-[0_10px_0_rgba(120,180,200,0.25)] hover:-translate-y-2'
                }`}
                style={isActive ? { boxShadow: `0 14px 0 ${cc.ring}, 0 0 0 3px ${cc.accent}` } : {}}
              >
                {/* Roof */}
                <div
                  className="absolute -top-8 left-1/2 -translate-x-1/2 w-[85%] h-16 rounded-t-[100px]"
                  style={{ background: cc.accent, clipPath: 'polygon(0 100%, 50% 0, 100% 100%)' }}
                />
                <div
                  className="absolute -top-4 right-6 rotate-6 font-brush text-[18px] px-4 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: cc.dark }}
                >
                  {p.age}
                </div>

                <div
                  className="w-20 h-20 mx-auto rounded-full flex items-center justify-center font-brush text-[36px] text-white shadow-inner"
                  style={{ background: cc.accent }}
                >
                  {i + 1}
                </div>
                <h3 className="mt-5 font-brush text-[34px] text-center text-[#3a2a1a]">{p.title}</h3>
                <p className="mt-3 font-hand text-[17px] text-center text-[#4a3b2a]/85 leading-snug">
                  {p.desc}
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-full text-[13px] font-hand"
                      style={{ background: cc.soft, color: cc.dark }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div
                  className="mt-6 mx-auto flex items-center justify-center gap-2 font-hand text-[15px] transition-all"
                  style={{ color: cc.dark }}
                >
                  {isActive ? (
                    <span className="inline-flex items-center gap-2">
                      <CheckCircle2 size={16} /> Detail ditampilkan
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 group-hover:gap-3">
                      Lihat detail <ArrowRight size={16} />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        <div
          key={active.id}
          className="mt-12 rounded-[36px] bg-white border-4 border-white shadow-[0_14px_0_rgba(120,180,200,0.25)] overflow-hidden animate-fade-up"
        >
          {/* Header banner */}
          <div
            className="relative px-8 md:px-12 py-10 md:py-12"
            style={{ background: `linear-gradient(135deg, ${c.soft} 0%, #ffffff 100%)` }}
          >
            <div className="absolute top-4 right-6 rotate-[-6deg] font-brush text-[24px] px-4 py-1.5 rounded-full text-white shadow" style={{ background: c.dark }}>
              {active.age}
            </div>
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center rotate-[-8deg]"
                style={{ background: c.accent }}
              >
                <School size={30} color="#fff" />
              </div>
              <div>
                <div className="font-hand text-[13px] tracking-[0.3em]" style={{ color: c.dark }}>
                  {active.subtitle}
                </div>
                <h3 className="font-brush text-[42px] md:text-[54px] leading-none" style={{ color: c.dark }}>
                  {active.title}
                </h3>
              </div>
            </div>
            <p className="mt-5 font-hand text-[19px] md:text-[22px] text-[#3a2a1a] max-w-[720px]">
              <Sparkles size={18} className="inline mr-1 mb-1" style={{ color: c.accent }} />
              {active.tagline}
            </p>

            {/* Quick facts */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Clock, label: 'Durasi', value: active.duration },
                { icon: Calendar, label: 'Hari', value: active.days },
                { icon: Users2, label: 'Rasio Kelas', value: active.ratio },
                { icon: Wallet, label: 'Biaya', value: active.price },
              ].map((f, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl px-4 py-3 flex items-center gap-3 border-2 border-white shadow-[0_5px_0_rgba(120,180,200,0.18)]"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: c.soft, color: c.dark }}
                  >
                    <f.icon size={18} />
                  </div>
                  <div className="leading-tight">
                    <div className="font-hand text-[12px] tracking-widest text-[#4a3b2a]/60 uppercase">{f.label}</div>
                    <div className="font-brush text-[19px] text-[#3a2a1a]">{f.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 md:px-12 py-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Goals + Facilities */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h4 className="font-brush text-[28px] text-[#c74a2a] flex items-center gap-2">
                  <Sparkles size={22} color="#ee7a3d" /> Tujuan Belajar
                </h4>
                <ul className="mt-4 space-y-3">
                  {active.goals.map((g, i) => (
                    <li key={i} className="flex items-start gap-3 font-hand text-[16px] text-[#3a2a1a]">
                      <span
                        className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: c.soft }}
                      >
                        <CheckCircle2 size={16} color={c.accent} />
                      </span>
                      {g}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-brush text-[28px] text-[#c74a2a] flex items-center gap-2">
                  <School size={22} color="#ee7a3d" /> Fasilitas Kelas
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {active.facilities.map((f) => (
                    <span
                      key={f}
                      className="px-4 py-2 rounded-full font-hand text-[14px] border-2"
                      style={{ background: '#fff', borderColor: c.soft, color: c.dark }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Subjects grid */}
            <div className="lg:col-span-3">
              <h4 className="font-brush text-[28px] text-[#2b8b98] flex items-center gap-2">
                <BookOpen size={22} color="#3aa9b7" /> Mata Pelajaran & Kegiatan
              </h4>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {active.subjects.map((s, i) => {
                  const Icon = iconMap[s.icon] || Sparkles;
                  return (
                    <div
                      key={i}
                      className="group rounded-2xl p-4 border-2 border-[#f1e6d2] hover:border-transparent hover:-translate-y-1 transition-all duration-300"
                      style={{ background: '#fff' }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 group-hover:rotate-[-8deg] transition-transform duration-300"
                          style={{ background: c.soft }}
                        >
                          <Icon size={22} color={c.accent} />
                        </div>
                        <div>
                          <div className="font-brush text-[20px] leading-tight" style={{ color: c.dark }}>
                            {s.name}
                          </div>
                          <div className="font-hand text-[14px] text-[#4a3b2a]/80 leading-snug">
                            {s.desc}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Daily Schedule */}
          <div className="px-6 md:px-12 pb-12">
            <h4 className="font-brush text-[28px] text-[#2b8b98] flex items-center gap-2">
              <Clock size={22} color="#3aa9b7" /> Jadwal Harian
            </h4>
            <div className="mt-6 relative">
              {/* Timeline line */}
              <div
                className="absolute left-4 md:left-6 top-2 bottom-2 w-1 rounded-full opacity-70"
                style={{ background: `repeating-linear-gradient(180deg, ${c.accent} 0 6px, transparent 6px 12px)` }}
              />
              <div className="space-y-3">
                {active.schedule.map((s, i) => (
                  <div key={i} className="relative pl-14 md:pl-20">
                    <div
                      className="absolute left-0 md:left-2 top-1 w-8 h-8 rounded-full flex items-center justify-center text-white font-hand text-[12px] shadow"
                      style={{ background: c.accent }}
                    >
                      {i + 1}
                    </div>
                    <div
                      className="rounded-2xl px-5 py-3 flex flex-col md:flex-row md:items-center gap-1 md:gap-4"
                      style={{ background: c.soft }}
                    >
                      <div className="font-brush text-[22px] w-24 shrink-0" style={{ color: c.dark }}>
                        {s.time}
                      </div>
                      <div className="font-hand text-[16px] text-[#3a2a1a]">{s.activity}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-3xl p-6" style={{ background: c.soft }}>
              <div>
                <div className="font-brush text-[26px]" style={{ color: c.dark }}>
                  Tertarik dengan program {active.title}?
                </div>
                <div className="font-hand text-[15px] text-[#4a3b2a]/80">
                  Ajak si kecil untuk trial class gratis, atau hubungi kami untuk info lengkap.
                </div>
              </div>
              <button
                onClick={() => document.getElementById('kontak')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-hand text-[16px] shadow-[0_5px_0_rgba(0,0,0,0.15)] hover:translate-y-[2px] hover:shadow-[0_3px_0_rgba(0,0,0,0.15)] transition-all"
                style={{ background: c.accent }}
              >
                Daftar Trial Class <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-20 overflow-hidden py-6 border-y-2 border-dashed border-[#c8dfe8]">
          <div className="flex whitespace-nowrap animate-marquee">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex items-center gap-8 pr-8 font-brush text-[42px] text-[#3aa9b7]">
                <span>Bermain</span>
                <span className="text-[#ee7a3d]">✦</span>
                <span className="text-[#c74a2a]">Belajar</span>
                <span className="text-[#ee7a3d]">✦</span>
                <span>Bertumbuh</span>
                <span className="text-[#ee7a3d]">✦</span>
                <span className="text-[#6fae4a]">Bersama</span>
                <span className="text-[#ee7a3d]">✦</span>
                <span>Bergembira</span>
                <span className="text-[#ee7a3d]">✦</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Programs;
